import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Grid,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

/**
 * Skeleton for dashboard stat cards
 */
export const StatCardSkeleton: React.FC = () => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }}
  >
    <CardContent>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Skeleton variant="rounded" width={60} height={24} />
        </Box>
        <Box>
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="60%" height={40} />
        </Box>
        <Skeleton variant="text" width="80%" height={16} />
      </Stack>
    </CardContent>
  </Card>
);

/**
 * Skeleton for a row of stat cards
 */
export const StatCardRowSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} sm={6} lg={3} key={index}>
        <StatCardSkeleton />
      </Grid>
    ))}
  </Grid>
);

/**
 * Skeleton for job cards
 */
export const JobCardSkeleton: React.FC = () => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s'
    }}
  >
    <CardContent>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={28} />
            <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="rounded" width={70} height={24} />
        </Box>

        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={80} height={24} />
          <Skeleton variant="rounded" width={100} height={24} />
          <Skeleton variant="rounded" width={90} height={24} />
        </Stack>

        <Skeleton variant="text" width="100%" height={16} />
        <Skeleton variant="text" width="90%" height={16} />
        <Skeleton variant="text" width="60%" height={16} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/**
 * Skeleton for a grid of job cards
 */
export const JobCardGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} md={6} lg={4} key={index}>
        <JobCardSkeleton />
      </Grid>
    ))}
  </Grid>
);

/**
 * Skeleton for table rows
 */
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 5 }) => (
  <TableRow>
    {Array.from({ length: columns }).map((_, index) => (
      <TableCell key={index}>
        <Skeleton variant="text" width={index === 0 ? '80%' : '60%'} height={24} />
      </TableCell>
    ))}
  </TableRow>
);

/**
 * Skeleton for a full table
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 5
}) => (
  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.50' }}>
          {Array.from({ length: columns }).map((_, index) => (
            <TableCell key={index}>
              <Skeleton variant="text" width="70%" height={20} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, index) => (
          <TableRowSkeleton key={index} columns={columns} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Skeleton for application detail view
 */
export const ApplicationDetailSkeleton: React.FC = () => (
  <Box>
    <Stack spacing={3}>
      {/* Header */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
          <Skeleton variant="circular" width={80} height={80} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="50%" height={32} />
            <Skeleton variant="text" width="30%" height={20} sx={{ mt: 1 }} />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={100} height={24} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={100} height={40} />
            <Skeleton variant="rounded" width={100} height={40} />
          </Stack>
        </Stack>
      </Paper>

      {/* Content Sections */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
            <Skeleton variant="text" width="90%" height={16} />
            <Skeleton variant="text" width="70%" height={16} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
            <Stack spacing={2}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i}>
                  <Skeleton variant="text" width="40%" height={16} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  </Box>
);

/**
 * Skeleton for form fields
 */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => (
  <Paper sx={{ p: 3, borderRadius: 2 }}>
    <Stack spacing={3}>
      <Skeleton variant="text" width="40%" height={32} />
      {Array.from({ length: fields }).map((_, index) => (
        <Box key={index}>
          <Skeleton variant="text" width="25%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={56} />
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Skeleton variant="rounded" width={100} height={40} />
        <Skeleton variant="rounded" width={120} height={40} />
      </Box>
    </Stack>
  </Paper>
);

/**
 * Skeleton for analytics charts
 */
export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <Paper sx={{ p: 3, borderRadius: 2 }}>
    <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" width="100%" height={height} />
  </Paper>
);

/**
 * Skeleton for profile/settings page
 */
export const ProfileSkeleton: React.FC = () => (
  <Stack spacing={3}>
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" spacing={3} alignItems="center">
        <Skeleton variant="circular" width={100} height={100} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="25%" height={20} sx={{ mt: 1 }} />
        </Box>
        <Skeleton variant="rounded" width={120} height={40} />
      </Stack>
    </Paper>
    <FormSkeleton fields={5} />
  </Stack>
);

/**
 * Skeleton for list items
 */
export const ListItemSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
    <Skeleton variant="circular" width={40} height={40} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="40%" height={16} />
    </Box>
    <Skeleton variant="rounded" width={80} height={32} />
  </Box>
);

/**
 * Skeleton for a list
 */
export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} sx={{ borderBottom: index < count - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
        <ListItemSkeleton />
      </Box>
    ))}
  </Paper>
);

/**
 * Page loading skeleton with header
 */
export const PageSkeleton: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Box>
    <Box sx={{ mb: 4 }}>
      <Skeleton variant="text" width="30%" height={40} />
      <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1 }} />
    </Box>
    {children || <TableSkeleton />}
  </Box>
);
